<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from './AppHeader.vue';
import AppSidebar from './AppSidebar.vue';
import AppFooter from './AppFooter.vue';
import BeaconRateLimitBanner from '@/components/super-admin/BeaconRateLimitBanner.vue';
import { useSidebar } from '@/composables/useSidebar';
import { buildLearnerNav, buildOrgAdminNav, superAdminNav } from '@/config/navigation';
import { useAccessibleLibraryStore, useUserStore } from '@/stores';

const route = useRoute();
const isSuperAdminRoute = computed(() => route.path.startsWith('/super-admin'));
const { wrapperClasses } = useSidebar();
const accessibleLibraryStore = useAccessibleLibraryStore();
const userStore = useUserStore();

// Load the signed-in user's accessible libraries so the Content Library nav
// lists exactly the libraries they can reach (learner and org admin alike).
// Non-blocking — the section stays empty until the fetch resolves, so first
// paint isn't delayed.
watch(
  () => userStore.isAuthenticated,
  (authed) => {
    if (authed) accessibleLibraryStore.ensureLoaded();
  },
  { immediate: true }
);

const navConfig = computed(() => {
  if (route.path.startsWith('/super-admin')) {
    return superAdminNav;
  }
  if (route.path.startsWith('/admin')) {
    return buildOrgAdminNav(accessibleLibraryStore.libraries);
  }
  return buildLearnerNav(accessibleLibraryStore.libraries, accessibleLibraryStore.loaded);
});
</script>

<template>
  <div class="app-shell">
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <AppHeader />

    <div class="main-container">
      <AppSidebar :nav-config="navConfig" />

      <div class="scrollable-wrapper" :class="wrapperClasses">
        <main id="main-content" class="main-content">
          <BeaconRateLimitBanner v-if="isSuperAdminRoute" />
          <slot />
        </main>

        <AppFooter />
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100vh;
}
</style>
